import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { backupService } from "@/services/backupService";
import type { BackupSettingsUpdateDto } from "@/types/backup.dto";
import { queryKeys } from "./keys";

export function useBackups() {
  return useQuery({
    queryKey: queryKeys.settings.backups,
    queryFn: () => backupService.getOverview(),
    // Backups change on the server (scheduled runs), so don't serve minutes-old data here.
    staleTime: 0,
    // Keep polling while a backup is running so the page shows when it finishes.
    refetchInterval: (query) => (query.state.data?.isRunning ? 3000 : false),
  });
}

export function useCreateBackupMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => backupService.create(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.settings.backups }),
  });
}

export function useUpdateBackupSettingsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: BackupSettingsUpdateDto) => backupService.updateSettings(dto),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.settings.backups }),
  });
}

export function useDeleteBackupMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileName: string) => backupService.remove(fileName),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.settings.backups }),
  });
}
